import { type ReactNode, useEffect, useRef, useState } from "react";
import { Button, type ButtonProps } from "../components/ui/button";
import { ApiError } from "../lib/api";
import { cn } from "../lib/utils";
import { DIFFUSION_PATH, RETRY, SESSION_FORBIDDEN, type Ton } from "./diffusion-entree";
import { SignIn } from "./sign-in";

/**
 * Ce que les ecrans de la diffusion partagent cote affichage : le ton des phrases, le lien a l'allure d'un bouton, le
 * bouton occupe qui garde le focus, la sortie du champ de texte qui a le focus, le focus qui va au resultat d'un
 * geste, et ce que montre un onglet dont la lecture a echoue. Les requetes (le delai de relecture, les ecritures) sont
 * dans `diffusion-api.ts`.
 */

/** La classe de chaque ton : le rouge est pour ce qui attend un geste de Nicolas, le gris pour ce qui est attendu. */
export const TONE_CLASSES: Record<Ton, string | undefined> = {
  ok: undefined,
  neutre: "text-muted-foreground",
  problem: "text-destructive",
};

/** Un lien a l'allure d'un bouton : `Button` rend un `<button>`, pas un lien. */
export const LINK_BUTTON =
  "inline-flex h-10 w-fit items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

/** La meme chose, a contour et en petit : pour un lien qui vaut un geste secondaire. */
export const LINK_BUTTON_OUTLINE =
  "inline-flex h-8 w-fit items-center justify-center rounded-md border border-input bg-background px-3 text-sm font-medium transition hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/** Un champ de saisie (texte, heure, liste) : bordure, focus visible, et un contour rouge quand il est signale. */
export const CHAMP =
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-[invalid=true]:border-destructive";

/**
 * Un bouton qui peut etre occupe : tant que son geste est en cours, il **garde le focus** et ne fait rien. Il porte
 * `aria-disabled` et non `disabled` : Chromium retire le focus d'un bouton qui devient desactive, et le clavier se
 * retrouvait sur la page. Les clics sont ignores (la touche Entree et la barre d'espace en sont), l'envoi du
 * formulaire qu'un bouton `submit` porte aussi. Les lecteurs d'ecran l'annoncent « indisponible ».
 */
export function BoutonOccupe({
  occupe,
  onClick,
  className,
  ...props
}: ButtonProps & { occupe: boolean }) {
  return (
    <Button
      {...props}
      aria-disabled={occupe ? true : undefined}
      className={cn("aria-disabled:cursor-wait aria-disabled:opacity-50!", className)}
      onClick={(event) => {
        if (occupe) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
    />
  );
}

/**
 * Donne le focus au resultat d'un geste, quand le geste l'a demande (`demander`) et que le resultat est la (`pret`) :
 * le bouton qu'on vient d'actionner a pu disparaitre avec la confirmation qu'il fermait, et le focus ne doit pas
 * tomber sur la page. La demande ne vaut que pour un resultat (une fois le focus donne, elle est rendue). `ref` va sur
 * un element focalisable sans entrer dans l'ordre de tabulation (`tabIndex={-1}`).
 *
 * `ancrage` dit a quoi le resultat appartient, la semaine qu'on regarde : la demande en garde l'ancrage, et **elle est
 * oubliee** des que l'ancrage change. Le resultat d'une preparation lancee sur une semaine ne prend pas le focus quand on
 * regarde une autre semaine, ni quand on revient a la premiere : personne n'a demande ce focus-la a ce moment-la.
 */
export function useFocusDuResultat<T extends HTMLElement>(pret: boolean, ancrage?: string) {
  const ref = useRef<T>(null);
  /** La demande en cours, avec l'ancrage ou elle a ete faite ; `null` sans demande. */
  const [demande, setDemande] = useState<{ readonly ancrage: string | undefined } | null>(null);
  useEffect(() => {
    if (demande === null) return;
    if (demande.ancrage !== ancrage) {
      setDemande(null);
      return;
    }
    if (pret) {
      ref.current?.focus();
      setDemande(null);
    }
  }, [demande, pret, ancrage]);
  return { ref, demander: () => setDemande({ ancrage }) };
}

/**
 * Quitte le champ de texte qui a le focus, ce qui l'enregistre (un champ de la semaine s'enregistre a sa sortie). Un
 * clic ne fait pas toujours sortir du champ : sur un iPhone, toucher un bouton laisse le clavier ouvert et le champ
 * garde le focus, donc rien n'est enregistre quand le clic arrive. Les gestes qui comptent sur ce que le serveur a
 * (valider la semaine, passer a une autre semaine ou a un autre onglet) l'appellent d'abord. Sans effet quand le focus
 * est ailleurs, sur un bouton par exemple : celui qui valide au clavier le garde.
 */
export function quitterLeChamp(): void {
  const actif = document.activeElement;
  if (
    actif instanceof HTMLTextAreaElement ||
    (actif instanceof HTMLInputElement && actif.type === "text")
  ) {
    actif.blur();
  }
}

/**
 * La garde du depart : ce que l'onglet « La semaine » demande avant qu'on la quitte (un autre onglet, une autre
 * semaine). Elle attend la file des ecritures, et rend faux s'il reste un champ dont le serveur n'a pas pris le texte
 * (refuse, ou sans reponse) : l'ecran est alors demonte avec lui, et sa phrase ne se verrait plus nulle part.
 */
let gardeDuDepart: (() => Promise<boolean>) | null = null;

/** Pose la garde du depart ; rend de quoi la retirer. Une seule semaine est a l'ecran a la fois. */
export function poserGardeDuDepart(garde: () => Promise<boolean>): () => void {
  gardeDuDepart = garde;
  return () => {
    if (gardeDuDepart === garde) gardeDuDepart = null;
  };
}

/**
 * Part (`partir`, un changement d'onglet ou de semaine) si la garde le permet. Quitte d'abord le champ qui a le focus
 * (son texte s'enregistre), puis laisse la garde juger. Sans garde (aucune semaine a l'ecran) on part tout de suite ;
 * sinon une fois les ecritures finies, et seulement si aucun texte n'a ete refuse : on reste alors, la phrase du champ
 * est a l'ecran et le focus dessus.
 */
export function peutPartir(partir: () => void): void {
  quitterLeChamp();
  const garde = gardeDuDepart;
  if (garde === null) {
    partir();
    return;
  }
  void garde().then((ok) => {
    if (ok) partir();
  });
}

/**
 * Ce que montre un onglet dont la lecture a echoue : le formulaire du lien magique quand la session est perdue
 * (401), une phrase et le meme formulaire quand l'adresse n'est pas admise (403), et sinon la phrase de l'onglet
 * avec un bouton pour recommencer.
 */
export function Echec({
  error,
  message,
  onRetry,
}: {
  error: Error | null;
  message: string;
  onRetry: () => void;
}): ReactNode {
  const status = error instanceof ApiError ? error.status : null;
  if (status === 401) {
    return <SignIn callbackURL={DIFFUSION_PATH} />;
  }
  if (status === 403) {
    // Pas d'impasse : le lien magique d'une autre adresse remplace la session de ce compte.
    return (
      <>
        <p className="text-sm">{SESSION_FORBIDDEN}</p>
        <SignIn callbackURL={DIFFUSION_PATH} />
      </>
    );
  }
  return (
    <div className="flex flex-col items-start gap-3">
      <p className="text-sm text-destructive" role="alert">
        {message}
      </p>
      <Button onClick={onRetry}>{RETRY}</Button>
    </div>
  );
}
